/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, BookOpen, Trash2 } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { HELP_CATEGORIES, labelFor } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function FirmHelp() {
    const { user } = useAuth();
    const [articles, setArticles] = useState([]);
    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (category !== "all") params.category = category;
            if (search) params.search = search;
            const { data } = await api.get("/help/", { params });
            setArticles(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-line */ }, [category, search]);

    async function del(id) {
        if (!confirm("Delete this article?")) return;
        try {
            await api.delete(`/help/${id}`);
            toast.success("Article deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div>
            <PageHeader
                eyebrow="Help Centre"
                title="Self-serve help library"
                description="Reduce repetitive client questions. Keywords power smart suggestions inside the client portal messaging."
                actions={user?.role === "firm_admin" || user?.role === "firm_staff" ? <NewArticleDialog onCreated={load} /> : null}
                testId="help-header"
            />

            <div className="co-card co-card-pad flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px] text-[#5E636E]">Search</Label>
                    <Input className="mt-1" placeholder="Search title, body or keyword…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="help-search" />
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="mt-1 min-w-[180px]" data-testid="help-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{HELP_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>

            <div className="mt-6">
                {loading ? <div className="text-sm text-[#5E636E]">Loading…</div> : articles.length === 0 ? (
                    <EmptyState icon={BookOpen} title="No articles" description="Add your first help article." testId="help-empty" />
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {articles.map((a) => (
                            <div key={a.id} className="co-card co-card-pad" data-testid={`help-card-${a.slug}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-[#5E636E]">{labelFor(HELP_CATEGORIES, a.category)}</div>
                                        <h3 className="font-display text-lg font-medium mt-1">{a.title}</h3>
                                    </div>
                                    {user?.role === "firm_admin" && (
                                        <button onClick={() => del(a.id)} className="text-[#5E636E] hover:text-rose-600" data-testid={`help-delete-${a.slug}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                <p className="mt-3 text-xs text-[#5E636E] line-clamp-4 whitespace-pre-line">{a.body}</p>
                                {a.keywords?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {a.keywords.slice(0, 6).map((k) => (
                                            <span key={k} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-mono">{k}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function NewArticleDialog({ onCreated }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        title: "", slug: "", category: "general", country: "NZ", body: "", keywords: "",
    });
    const [busy, setBusy] = useState(false);
    const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.title || !form.slug || !form.body) return;
        setBusy(true);
        try {
            await api.post("/help/", {
                ...form,
                keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
            });
            toast.success("Article published");
            setOpen(false);
            setForm({ title: "", slug: "", category: "general", country: "NZ", body: "", keywords: "" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="new-article-btn"><Plus className="h-4 w-4" /> New article</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="new-article-dialog">
                <DialogHeader><DialogTitle>New help article</DialogTitle></DialogHeader>
                <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={(e) => s("title", e.target.value)} data-testid="na-title" /></div>
                        <div><Label>Slug</Label><Input className="mt-1" placeholder="e.g. reset-authenticator" value={form.slug} onChange={(e) => s("slug", e.target.value)} data-testid="na-slug" /></div>
                        <div>
                            <Label>Category</Label>
                            <Select value={form.category} onValueChange={(v) => s("category", v)}>
                                <SelectTrigger className="mt-1" data-testid="na-category"><SelectValue /></SelectTrigger>
                                <SelectContent>{HELP_CATEGORIES.filter((c) => c.value !== "all").map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div><Label>Body</Label><Textarea rows={8} className="mt-1 font-mono text-sm" value={form.body} onChange={(e) => s("body", e.target.value)} data-testid="na-body" /></div>
                    <div><Label>Keywords (comma separated)</Label><Input className="mt-1" placeholder="bank statement, asb, westpac" value={form.keywords} onChange={(e) => s("keywords", e.target.value)} data-testid="na-keywords" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="na-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="na-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
